import React from 'react';
import { useSelector } from 'react-redux';
import flatten from 'lodash/flatten';

// Components
import WpAlert from 'Wpds/components/alert/WpAlert';
import RecurrenceSettingsInput from 'Dashboard/components/RecurrenceSettingsInput/RecurrenceSettingsInput';

// Styles
import './FeedbackRecognitionSettingsControl.scss';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Types
import { Workspace } from 'Shared/types/Workspace.type';

const recurrenceIntervalOptions = [{ value: 'week', label: 'Week(s)' }];

const maxTimeWithoutPromptUnitOptions = [
  { value: 'week', label: 'Week(s)' },
  { value: 'month', label: 'Month(s)' },
];

const defaultWeekCountInDays = 7;
const defaultMonthCountInDays = 28;

type Props = {
  formValues: {
    recurrenceAmount: number;
    recurrenceInterval: string;
    maxTimeWithoutPromptAmount: number;
    maxTimeWithoutPromptUnit: string;
  };
  onAmountValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onIntervalValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxTimeWithoutPromptAmountChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxTimeWithoutPromptUnitChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  promptName: string;
};

function FeedbackRecognitionSettingsControl({
  formValues,
  onAmountValueChange,
  onIntervalValueChange,
  onMaxTimeWithoutPromptAmountChange,
  onMaxTimeWithoutPromptUnitChange,
  promptName,
}: Props) {
  const { id: currentUserId } = useCurrentUser();

  const teammatesCount = useSelector((state: { workspaces: Workspace[] }) => {
    const workspaces = state.workspaces.filter(({ isGroup, isPersonal }) => !isGroup && !isPersonal);
    const workspaceUsers = flatten(workspaces.map(({ users }) => users)).filter(({ id }) => id !== currentUserId);
    return workspaceUsers.length;
  });

  const recurrenceIntervalOption = recurrenceIntervalOptions.find(
    ({ value }) => value === formValues.recurrenceInterval
  );

  const maxTimeWithoutPromptUnitOption = maxTimeWithoutPromptUnitOptions.find(
    ({ value }) => value === formValues.maxTimeWithoutPromptUnit
  );

  const recurrenceAmountInDays =
    recurrenceIntervalOption.value === 'week'
      ? formValues.recurrenceAmount * defaultWeekCountInDays
      : formValues.recurrenceAmount * defaultMonthCountInDays;

  const maxTimeWithoutPromptInDays =
    maxTimeWithoutPromptUnitOption.value === 'week'
      ? formValues.maxTimeWithoutPromptAmount * defaultWeekCountInDays
      : formValues.maxTimeWithoutPromptAmount * defaultMonthCountInDays;

  const teammatePerWeek = Math.min(
    teammatesCount,
    Math.ceil(teammatesCount * (recurrenceAmountInDays / maxTimeWithoutPromptInDays))
  );

  return (
    <div className="feedback-settings-control d-flex flex-column mb-4 w-100">
      <div className="feedback-settings-control-title font-weight-bold mb-2">
        {`How often do you want to be prompted to give ${promptName}?`}
      </div>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <div className="d-flex align-items-center mb-2">
          <RecurrenceSettingsInput
            label="Every"
            amountValue={formValues.recurrenceAmount}
            intervalValue={recurrenceIntervalOption.value}
            intervalOptions={recurrenceIntervalOptions}
            onAmountValueChange={onAmountValueChange}
            onIntervalValueChange={onIntervalValueChange}
          />
        </div>
      </div>
      <div className="feedback-settings-control-title font-weight-bold mb-2">
        {`How long can a teammate go without ${promptName} from you?`}
      </div>
      <div className="d-flex mb-2">
        <RecurrenceSettingsInput
          amountValue={formValues.maxTimeWithoutPromptAmount}
          intervalValue={maxTimeWithoutPromptUnitOption.value}
          intervalOptions={maxTimeWithoutPromptUnitOptions}
          onAmountValueChange={onMaxTimeWithoutPromptAmountChange}
          onIntervalValueChange={onMaxTimeWithoutPromptUnitChange}
        />
      </div>
      <div className="mt-2">
        <WpAlert type="info" showIcon={false}>
          {`You have ${teammatesCount} teammates. 
          That means you'll be prompted to give ${teammatePerWeek} teammates ${promptName} each week.`}
        </WpAlert>
      </div>
    </div>
  );
}

export default FeedbackRecognitionSettingsControl;
